import { formatNameFirstLastInitial } from '../utils/userState';
import { getStepShareMeta } from '../config/stepShareConfig';
import { STEP2_QUESTIONS, STEP3_QUESTIONS, type QuestionData } from '../utils/questionMapping';
import { Step1WritingMetadata } from '../types/step1Writing';
import { Step5InventoryPresentationRecord } from '../types/step5InventoryPresentation';
import {
  TRADITIONAL_SEVENTH_STEP_PRAYER,
  TRADITIONAL_SEVENTH_STEP_PRAYER_SOURCE,
} from '../content/traditionalSeventhStepPrayer';
import { renderStep1WritingProgressHtml } from './firstStepShareHtml';

interface SummaryUser {
  email?: string;
  name?: string;
}

export interface InventoryItem {
  who?: string;
  why?: string;
  feeling?: string;
  impacts?: string[];
  myPart?: string;
  fear?: string;
  whoHarmed?: string;
  how?: string;
  defects?: string[];
  alternative?: string;
}

export interface AmendsPerson {
  name: string;
  note?: string;
  timing?: string;
  form?: string;
  amendsPlan?: string;
  amendsCompleted?: boolean;
  amendsExperience?: string;
}

export interface Step10Entry {
  date: string;
  note: string;
}

export interface DefectAnswers {
  hurts: string;
  helps: string;
  less: string;
}

export interface StepSummaryContext {
  stepNumber: number;
  user: SummaryUser;
  actions: { id: number; completed: boolean }[];
  questionAnswers?: QuestionData;
  prayerText?: string;
  resentments?: InventoryItem[];
  fears?: InventoryItem[];
  harms?: InventoryItem[];
  characterDefects?: string[];
  step6Defects?: string[];
  step6DefectAnswers?: Record<string, DefectAnswers>;
  step7Prayer?: string;
  amendsPersons?: AmendsPerson[];
  dailyInventories?: Step10Entry[];
  quietTimePlan?: string;
  quietTimePracticed3Days?: boolean;
  servicePlan?: string;
  step1Writing?: Step1WritingMetadata;
  step5InventoryPresentation?: Step5InventoryPresentationRecord;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function preserveNewlines(text: string): string {
  return escapeHtml(text).replace(/\n/g, '<br>');
}

function formatDate(): string {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const SUMMARY_STYLES = `
        body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 24px; color: #111; line-height: 1.5; }
        h1 { font-size: 1.5rem; margin-bottom: 0.25rem; }
        .meta { color: #555; font-size: 0.95rem; margin-bottom: 1.5rem; }
        h2 { font-size: 1.15rem; margin: 1.25rem 0 0.5rem; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem; }
        h3 { font-size: 1rem; margin: 0.75rem 0 0.35rem; color: #333; }
        .section { margin-bottom: 1rem; }
        ul { margin: 0.25rem 0 0.75rem 1.25rem; }
        .completed-list li { margin-bottom: 0.35rem; }
        .remaining-list li { margin-bottom: 0.35rem; color: #555; }
        .qa-block { margin-bottom: 1rem; }
        .qa-label { font-weight: bold; margin-bottom: 0.25rem; }
        .qa-answer { margin-left: 0.5rem; margin-bottom: 0.5rem; white-space: pre-wrap; }
        .entry { margin-bottom: 1rem; padding: 10px; border: 1px solid #ddd; border-radius: 4px; }
        .entry-header { font-weight: bold; margin-bottom: 0.35rem; }
        .field { margin-bottom: 0.25rem; }
        .field-label { font-weight: bold; }
        .privacy { margin-top: 2rem; padding: 12px; background: #fff3cd; border-left: 4px solid #ffc107; font-size: 0.9rem; }
        .footer { text-align: center; margin-top: 2rem; font-size: 0.85rem; color: #666; }
        @media print { body { margin: 0.5in; } }
`;

function renderActionLists(
  actionLabels: Record<number, string>,
  actions: { id: number; completed: boolean }[]
): { completedHtml: string; remainingHtml: string } {
  const order = Object.keys(actionLabels)
    .map(Number)
    .sort((a, b) => a - b);

  const isCompleted = (id: number) => actions.some((a) => a.id === id && a.completed);

  const completedLines = order
    .filter((id) => isCompleted(id))
    .map((id) => `<li>${id}. ${escapeHtml(actionLabels[id])}</li>`);

  const remainingLines = order
    .filter((id) => !isCompleted(id))
    .map((id) => `<li>${id}. ${escapeHtml(actionLabels[id])}</li>`);

  const completedHtml =
    completedLines.length > 0
      ? `<div class="section"><h2>Completed work</h2><ul class="completed-list">${completedLines.join('')}</ul></div>`
      : '';

  const remainingHtml =
    remainingLines.length > 0
      ? `<div class="section"><h2>Remaining work</h2><ul class="remaining-list">${remainingLines.join('')}</ul></div>`
      : '';

  return { completedHtml, remainingHtml };
}

function renderQuestionsSection(
  title: string,
  questions: { id: string; label: string }[],
  answers: QuestionData
): string {
  const blocks = questions
    .map((q) => {
      const answer = answers[q.id]?.answer?.trim();
      if (!answer) return '';
      return `
        <div class="qa-block">
          <div class="qa-label">${escapeHtml(q.label)}</div>
          <div class="qa-answer">${preserveNewlines(answer)}</div>
        </div>`;
    })
    .filter(Boolean)
    .join('');

  if (!blocks) return '';
  return `<div class="section"><h2>${escapeHtml(title)}</h2>${blocks}</div>`;
}

function renderInventoriesBody(
  resentments: InventoryItem[],
  fears: InventoryItem[],
  harms: InventoryItem[]
): string {
  let html = '';

  if (resentments.length > 0) {
    html += `<div class="section"><h2>Resentments (${resentments.length})</h2>`;
    resentments.forEach((item) => {
      html += `
        <div class="entry">
          <div class="entry-header">${escapeHtml(item.who || 'Unknown')}</div>
          <div class="field"><span class="field-label">Why:</span> ${preserveNewlines(item.why || '')}</div>
          <div class="field"><span class="field-label">Feelings:</span> ${preserveNewlines(item.feeling || '')}</div>
          <div class="field"><span class="field-label">Impacted:</span> ${escapeHtml(item.impacts?.join(', ') || '')}</div>
          <div class="field"><span class="field-label">My Part:</span> ${preserveNewlines(item.myPart || '')}</div>
        </div>`;
    });
    html += '</div>';
  }

  if (fears.length > 0) {
    html += `<div class="section"><h2>Fears (${fears.length})</h2>`;
    fears.forEach((item) => {
      html += `
        <div class="entry">
          <div class="entry-header">${escapeHtml(item.fear || 'Unknown')}</div>
          <div class="field"><span class="field-label">Why:</span> ${preserveNewlines(item.why || '')}</div>
          <div class="field"><span class="field-label">Impacted:</span> ${escapeHtml(item.impacts?.join(', ') || '')}</div>
        </div>`;
    });
    html += '</div>';
  }

  if (harms.length > 0) {
    html += `<div class="section"><h2>Harms Done (${harms.length})</h2>`;
    harms.forEach((item) => {
      html += `
        <div class="entry">
          <div class="entry-header">${escapeHtml(item.whoHarmed || 'Unknown')}</div>
          <div class="field"><span class="field-label">How:</span> ${preserveNewlines(item.how || '')}</div>
          <div class="field"><span class="field-label">Defects:</span> ${escapeHtml(item.defects?.join(', ') || '')}</div>
          <div class="field"><span class="field-label">Alternative:</span> ${preserveNewlines(item.alternative || '')}</div>
        </div>`;
    });
    html += '</div>';
  }

  return html;
}

function renderCharacterDefects(defects: string[]): string {
  if (defects.length === 0) return '';
  const lis = defects.map((d) => `<li>${escapeHtml(d)}</li>`).join('');
  return `<div class="section"><h2>Character defects</h2><ul>${lis}</ul></div>`;
}

function formatPresentationDate(isoDate: string): string {
  if (!isoDate) return '';
  try {
    return new Date(`${isoDate}T12:00:00`).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

function renderStep5InventoryPresentation(p: Step5InventoryPresentationRecord): string {
  const startedDate = p.startedDate?.trim() || '';
  const place = p.place?.trim() || '';
  const reflection = p.reflection?.trim() || '';
  if (!startedDate && !place && !reflection) return '';

  let html =
    '<div class="section"><h2>Present Step Four inventories to my sponsor</h2>';
  if (startedDate) {
    html += `<div class="field"><span class="field-label">Date started:</span> ${escapeHtml(formatPresentationDate(startedDate))}</div>`;
  }
  if (place) {
    html += `<div class="field"><span class="field-label">Place(s):</span> ${preserveNewlines(place)}</div>`;
  }
  if (reflection) {
    html += `<div class="field"><span class="field-label">How it went / how I feel:</span> ${preserveNewlines(reflection)}</div>`;
  }
  html += '</div>';
  return html;
}

function renderStep6DefectWork(
  defects: string[],
  answers: Record<string, DefectAnswers>
): string {
  if (defects.length === 0 && Object.keys(answers).length === 0) return '';

  let html = '<div class="section"><h2>Character defect work</h2>';
  const names = defects.length > 0 ? defects : Object.keys(answers);

  names.forEach((defect) => {
    const a = answers[defect];
    if (!a) {
      html += `<div class="entry"><div class="entry-header">${escapeHtml(defect)}</div><p><em>Not yet answered</em></p></div>`;
      return;
    }
    html += `
      <div class="entry">
        <div class="entry-header">${escapeHtml(defect)}</div>
        <div class="field"><span class="field-label">How does this defect hurt me?</span> ${preserveNewlines(a.hurts || '')}</div>
        <div class="field"><span class="field-label">How does it help me?</span> ${preserveNewlines(a.helps || '')}</div>
        <div class="field"><span class="field-label">What would my life look like with less of this?</span> ${preserveNewlines(a.less || '')}</div>
      </div>`;
  });
  html += '</div>';
  return html;
}

function renderAmendsList(title: string, persons: AmendsPerson[]): string {
  if (persons.length === 0) return '';
  let html = `<div class="section"><h2>${escapeHtml(title)} (${persons.length})</h2>`;
  persons.forEach((p) => {
    html += `
      <div class="entry">
        <div class="entry-header">${escapeHtml(p.name)}</div>
        ${p.note ? `<div class="field"><span class="field-label">Note:</span> ${preserveNewlines(p.note)}</div>` : ''}
        ${p.timing ? `<div class="field"><span class="field-label">Willingness / timing:</span> ${escapeHtml(p.timing)}</div>` : ''}
        ${p.form ? `<div class="field"><span class="field-label">Form:</span> ${escapeHtml(p.form)}</div>` : ''}
        ${p.amendsPlan ? `<div class="field"><span class="field-label">Amends plan:</span> ${preserveNewlines(p.amendsPlan)}</div>` : ''}
        ${p.amendsCompleted !== undefined ? `<div class="field"><span class="field-label">Amends completed:</span> ${p.amendsCompleted ? 'Yes' : 'No'}</div>` : ''}
        ${p.amendsExperience ? `<div class="field"><span class="field-label">Experience:</span> ${preserveNewlines(p.amendsExperience)}</div>` : ''}
      </div>`;
  });
  html += '</div>';
  return html;
}

function renderDailyInventories(entries: Step10Entry[]): string {
  if (entries.length === 0) return '';
  let html = `<div class="section"><h2>Daily inventory (${entries.length})</h2>`;
  entries.forEach((e) => {
    html += `
      <div class="entry">
        <div class="entry-header">${escapeHtml(e.date || 'Untitled')}</div>
        <div class="field">${preserveNewlines(e.note || '')}</div>
      </div>`;
  });
  html += '</div>';
  return html;
}

function renderPlanText(title: string, text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  return `<div class="section"><h2>${escapeHtml(title)}</h2><div class="qa-answer">${preserveNewlines(trimmed)}</div></div>`;
}

function renderStep7PrayerSection(customPrayer?: string): string {
  const trimmed = customPrayer?.trim() || '';
  if (trimmed) {
    return renderPlanText('Seventh Step Prayer (in my own words)', trimmed);
  }
  return `<div class="section">
    <h2>Seventh Step Prayer</h2>
    <p class="qa-answer" style="margin-bottom: 1rem; color: #444;">
      I'm going with the traditional Seventh Step Prayer from the ${escapeHtml(TRADITIONAL_SEVENTH_STEP_PRAYER_SOURCE)}.
    </p>
    <div class="qa-answer">${preserveNewlines(TRADITIONAL_SEVENTH_STEP_PRAYER)}</div>
  </div>`;
}

function renderStep11QuietTimeShare(plan?: string, practiced3Days?: boolean): string {
  const trimmed = plan?.trim() || '';
  let html = trimmed ? renderPlanText('Quiet time plan', trimmed) : '';
  if (practiced3Days) {
    html += `<div class="section"><h2>Daily practice</h2><div class="qa-answer">I have practiced my daily quiet time plan for 3+ days.</div></div>`;
  }
  return html;
}

function renderStepBody(ctx: StepSummaryContext): string {
  const n = ctx.stepNumber;

  switch (n) {
    case 1:
      return renderStep1WritingProgressHtml(ctx.step1Writing);
    case 2:
      return renderQuestionsSection(
        'Step Two questions',
        STEP2_QUESTIONS,
        ctx.questionAnswers || {}
      );
    case 3: {
      const q = renderQuestionsSection('Step Three questions', STEP3_QUESTIONS, ctx.questionAnswers || {});
      const prayer = ctx.prayerText?.trim()
        ? renderPlanText('Third Step Prayer', ctx.prayerText)
        : '';
      return q + prayer;
    }
    case 4:
      return renderInventoriesBody(ctx.resentments || [], ctx.fears || [], ctx.harms || []);
    case 5:
      return (
        renderStep5InventoryPresentation(ctx.step5InventoryPresentation || {}) +
        renderCharacterDefects(ctx.characterDefects || [])
      );
    case 6:
      return renderStep6DefectWork(ctx.step6Defects || [], ctx.step6DefectAnswers || {});
    case 7:
      return renderStep7PrayerSection(ctx.step7Prayer);
    case 8:
    case 9:
      return renderAmendsList(n === 8 ? 'Amends list (Step Eight)' : 'Amends list (Step Nine)', ctx.amendsPersons || []);
    case 10:
      return renderDailyInventories(ctx.dailyInventories || []);
    case 11:
      return renderStep11QuietTimeShare(ctx.quietTimePlan, ctx.quietTimePracticed3Days);
    case 12:
      return renderPlanText('Service plan', ctx.servicePlan || '');
    default:
      return '';
  }
}

export function generateStepSummaryHTML(ctx: StepSummaryContext): string {
  const meta = getStepShareMeta(ctx.stepNumber);
  const memberName = ctx.user.name
    ? formatNameFirstLastInitial(ctx.user.name, 'Recovery Commons member')
    : 'Recovery Commons member';
  const currentDate = formatDate();
  const { completedHtml, remainingHtml } = renderActionLists(meta.actionLabels, ctx.actions);
  const bodyHtml = renderStepBody(ctx);

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${escapeHtml(meta.stepTitle)} — Recovery Commons</title>
    <script>
        window.addEventListener('load', function() {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('print') === 'true') {
                setTimeout(function() { window.print(); }, 500);
            }
        });
    </script>
    <style>${SUMMARY_STYLES}</style>
</head>
<body>
    <h1>${escapeHtml(meta.stepTitle)} — step work progress</h1>
    <div class="meta">${escapeHtml(memberName)} · ${currentDate}</div>
    ${completedHtml}
    ${remainingHtml}
    ${bodyHtml}
    <div class="privacy">
      <strong>Privacy notice:</strong> This summary link expires 7 days after it was created. After that date, the link will no longer work and the summary will be deleted.
    </div>
    <div class="footer">Recovery Commons (recoverycommons.com)</div>
</body>
</html>`;
}
